import { reviewsActions } from '@/actions';
import { server } from '@/helpers';
import { TIME_FILTER } from '@/constants/timeConstants';
import { useAppDispatch } from '@/hooks';
import { normalizeReviewListItems } from '@/helpers/normalize';
import { ReviewsState } from '@/reducers/reviewsReducer';
import { isOlderThanOneYear } from '@/helpers/dateTs';
import {
  ReviewsListResponse,
  ReviewDetails,
  ReviewsSearchFilter,
  ReviewsSearchResult,
  SelectedTimeFilterOptions,
  Review,
} from '@/types/reviews';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { modalActions } from '@/actions/modalActions';
import { ReviewsModalType } from '@/types/modal';

import { FormState } from '@/reducers/formReducer';
import { formActions } from '@/actions/formActions';

export const REVIEWS_PAGE_LIMIT = 3;
export const REVIEWS_MODAL_LIMIT = 30;

const EMPTY_SEARCH_RESULT: ReviewsSearchResult = { loading: false, fetched: false, count: 0, loadingByType: {} };

const useReviews = ({
  uid,
  searchFilter,
  initiateState,
  originType,
  enabled = true,
}: {
  uid: string;
  searchFilter?: ReviewsSearchFilter;
  initiateState?: boolean;
  originType?: 'placePage' | 'reviewsModal' | 'dealsPage';
  enabled?: boolean;
}) => {
  const reduxDispatch = useAppDispatch();

  const searchFilterFromState = useSelector((state: { form: FormState }) => state.form[uid]?.searchFilter);

  const reviewModal = useSelector(
    (state: {
      modal: {
        reviews: ReviewsModalType;
      };
    }) => state.modal.reviews,
  );

  const searchResultFromState = useSelector(
    (state: { reviews: ReviewsState }) => state.reviews.byUid[uid]?.searchResult || EMPTY_SEARCH_RESULT,
  );

  async function fetchReviewsList(params: ReviewsSearchFilter, concat?: boolean) {
    const result: ReviewsListResponse = await server.request
      .get(`/reviews/v2/list`, {
        params: { ...params, timeFilter: TIME_FILTER.FOREVER },
      })
      .then(server.handleSuccess)
      .catch((e) => {
        server.handleError(e);
      });

    const normalizedReviewListItems = normalizeReviewListItems(result.items);

    const olderReviews = result.items.filter((review: Review) => isOlderThanOneYear(review.createdAt));
    const newerReviews = result.items.filter((review: Review) => !isOlderThanOneYear(review.createdAt));
    reduxDispatch(
      reviewsActions.setReviewListItems(
        uid,
        {
          ...normalizedReviewListItems,
          ids: normalizedReviewListItems.ids.map((id) => String(id)),
          olderIds: olderReviews.map((review: Review) => String(review.id)),
          newerIds: newerReviews.map((review: Review) => String(review.id)),
        },
        concat,
      ),
    );

    return result;
  }

  async function fetchReviewsDetails(params?: ReviewsSearchFilter) {
    const result: { reviews: ReviewDetails } = await server.request
      .get(`/reviews/v2/details`, { params: { ...params, limit: 1 } })
      .then(server.handleSuccess)
      .catch(server.handleError);

    reduxDispatch(reviewsActions.setReviewDetails(uid, result.reviews));

    return result.reviews;
  }

  const fetchAllData = async (
    params: ReviewsSearchFilter,
    { concat, changedFilter }: { concat?: boolean; changedFilter?: string } = {},
  ) => {
    reduxDispatch(
      reviewsActions.setReviewsSearchResult(uid, {
        loading: true,
        fetched: false,
        loadingByType: { [changedFilter]: true },
      }),
    );

    const [reviewsList, reviewsDetails] = await Promise.all([
      fetchReviewsList(params, concat),
      fetchReviewsDetails(params),
    ]).catch((e) => {
      server.handleError(e);
      reduxDispatch(
        reviewsActions.setReviewsSearchResult(uid, {
          loading: false,
          fetched: true,
          count: 0,
          error: true,
          loadingByType: { [changedFilter]: false },
        }),
      );
      return [
        { items: [], count: 0, nextPage: undefined },
        {
          employeeStats: {},
        },
      ] as [ReviewsListResponse, ReviewDetails];
    });

    reduxDispatch(
      reviewsActions.setReviewsSearchResult(uid, {
        loading: false,
        fetched: true,
        loadingByType: { [changedFilter]: false },
        count: reviewsList.count,
        nextPage: reviewsList.nextPage,
      }),
    );
    return { reviewsList, reviewsDetails };
  };

  useEffect(() => {
    if (!enabled || !initiateState || !uid || !searchFilter) {
      return;
    }

    reduxDispatch(formActions.formInit({ searchFilter }, uid));

    if (originType) {
      fetchAllData(searchFilter);
    }

    // eslint-disable-next-line
  }, [enabled, searchFilter?.placeId]);

  return {
    openReviewsModal: (data: ReviewsModalType) => {
      reduxDispatch(modalActions.reviews({ ...data }));
    },
    setTimeFilter: (timeFilter: SelectedTimeFilterOptions) => {
      const newSearchFilter = { ...searchFilterFromState, page: 1, timeFilter, offset: 0 };
      reduxDispatch(formActions.fieldChanged('searchFilter', newSearchFilter, uid));

      fetchAllData(newSearchFilter, { changedFilter: 'timeFilter' }).then(({ reviewsDetails }) => {
        const selectedEmployeeId = newSearchFilter.employeeId;
        const selectedEmployeeHasNoReviews =
          originType === 'reviewsModal' &&
          !!selectedEmployeeId &&
          !reviewsDetails?.employeeStats?.[selectedEmployeeId]?.count;

        // The employee filter no longer matches anyone under the new time range — fall back
        // to showing all employees for the service rather than an empty result.
        if (selectedEmployeeHasNoReviews) {
          const resetSearchFilter = { ...newSearchFilter, employeeId: undefined };
          reduxDispatch(formActions.fieldChanged('searchFilter', resetSearchFilter, uid));
          fetchAllData(resetSearchFilter, { changedFilter: 'timeFilter' });
        }
      });
    },
    setRatingFilter: (rating: string) => {
      const newRating = searchFilterFromState.rating === rating ? undefined : rating;
      const newSearchFilter = {
        ...searchFilterFromState,
        offset: 0,
        page: 1,
        rating: newRating,
      };
      reduxDispatch(formActions.fieldChanged('searchFilter', newSearchFilter, uid));

      fetchAllData(newSearchFilter, { changedFilter: 'rating' });
    },

    setEmployeeServiceFilter: (employeeId?: number, serviceId?: number) => {
      const newSearchFilter = {
        ...searchFilterFromState,
        offset: 0,
        page: 1,
        employeeId,
        serviceId,
      };
      reduxDispatch(formActions.fieldChanged('searchFilter', newSearchFilter, uid));

      fetchAllData(newSearchFilter, { changedFilter: 'employeeService' });
    },
    switchFocus: (args: { focus: 'employee' | 'service' | 'rating'; employeeId?: number; serviceId?: number }) => {
      const { focus, employeeId, serviceId } = args;
      reduxDispatch(
        modalActions.reviews({
          focus,
          employeeId,
          serviceId,
          show: true,
        }),
      );
      const newSearchFilter = {
        ...searchFilterFromState,
        offset: 0,
        page: 1,
        serviceId,
        employeeId,
      };
      reduxDispatch(formActions.fieldChanged('searchFilter', newSearchFilter, uid));
      fetchAllData(newSearchFilter, { changedFilter: 'switchFocus' });
    },
    setPrevFocusState: (args: {
      focus: 'employee' | 'service' | 'rating';
      employeeId?: number;
      serviceId?: number;
      timeFilter: SelectedTimeFilterOptions;
      limit?: number;
    }) => {
      reduxDispatch(modalActions.addReviewsPrevState({ ...args }));
    },
    navigateToPreviousFocus: () => {
      const prevState = reviewModal.prevState || [];
      const lastState = prevState[prevState.length - 1];

      if (!lastState) {
        return;
      }
      const { focus, employeeId, serviceId, timeFilter, limit } = lastState;
      reduxDispatch(
        modalActions.reviews({
          focus,
          employeeId,
          serviceId,
          timeFilter,
          limit,
        }),
      );
      const newSearchFilter = {
        ...searchFilterFromState,
        ...lastState,
      };
      reduxDispatch(formActions.fieldChanged('searchFilter', newSearchFilter, uid));
      fetchAllData(newSearchFilter, { changedFilter: 'switchFocus' });
      reduxDispatch(modalActions.removeReviewsPrevState(lastState.id));
    },
    resetState: () => {
      reduxDispatch(reviewsActions.resetReviewsState(uid));
      reduxDispatch(modalActions.resetReviewsPrevState());
      reduxDispatch(formActions.terminateForm(uid));
    },
    handleClickNextPage: (limit: number) => {
      const newSearchFilter = { ...searchFilterFromState, page: searchFilterFromState.page + 1, limit };
      reduxDispatch(formActions.fieldChanged('searchFilter', newSearchFilter, uid));
      fetchAllData(newSearchFilter, { changedFilter: 'nextPage' });
    },
    handleLoadMore: (numberOfItems: number) => {
      const currentStart =
        searchFilterFromState.offset || (searchFilterFromState.page - 1) * searchFilterFromState.limit;

      const newSearchFilter = {
        ...searchFilterFromState,
        page: searchFilterFromState.page + 1,
        offset: currentStart + searchFilterFromState.limit,
        limit: numberOfItems,
      };
      reduxDispatch(formActions.fieldChanged('searchFilter', newSearchFilter, uid));
      const concat = true;
      const changedFilter = 'loadMore';

      fetchAllData(newSearchFilter, { concat, changedFilter });
    },
    fetchAllReviewsData: fetchAllData,
    totalPages: Math.ceil((searchResultFromState?.count || 0) / REVIEWS_PAGE_LIMIT),
    searchResult: searchResultFromState,
    searchFilter: searchFilterFromState,
  };
};

export default useReviews;
